if (!customElements.get('lc-accessories')) customElements.define('lc-accessories', class extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    const source = this.querySelector('[data-lca-source]');
    const chapters = this.querySelector('[data-lca-chapters]');
    if (!source || !chapters) return;
    const normal = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const classify = text => {
      const s = normal(text).replace(/:$/, '');
      if (/why.*love|benefits|features|pourquoi|bienfaits|caracteristiques|recommendation|recommandation/.test(s)) return 'love';
      if (/materials|material|composition|fabric|craftsmanship|matiere|^details$/.test(s)) return 'materials';
      if (/size|sizing|dimensions|choose your|taille|fit guide/.test(s)) return 'size';
      if (/best for|recommended for|ideal for|suitable for|who.*for|pour qui/.test(s)) return 'fit';
      if (/how to clean|cleaning|^care$|care instructions|washing|entretien|brush care|taking care/.test(s)) return 'care';
      if (/how to|directions|use|usage|utilisation|ritual/.test(s)) return 'use';
      if (/precaution|warning|caution|safety|mise en garde/.test(s)) return 'precautions';
      if (/faq|questions/.test(s)) return 'faq';
      return 'other';
    };
    const boundaries = [...source.querySelectorAll('h1,h2,h3,h4,h5,h6,p')].filter(el => /^H[1-6]$/.test(el.tagName) || (el.children.length === 1 && ['STRONG', 'B'].includes(el.firstElementChild.tagName) && /^(care|care instructions|entretien)$/i.test(el.textContent.trim())));
    if (!boundaries.length) { this.refineContent(source, 'overview'); this.dataset.ready = 'true'; return; }
    const groups = new Map();
    const intro = document.createRange(); intro.setStart(source, 0); intro.setEndBefore(boundaries[0]);
    const introduction = intro.cloneContents();
    boundaries.forEach((heading, i) => {
      const key = classify(heading.textContent);
      const range = document.createRange();
      if (key === 'other' || key === 'faq') range.setStartBefore(heading); else range.setStartAfter(heading);
      if (boundaries[i + 1]) range.setEndBefore(boundaries[i + 1]); else range.setEnd(source, source.childNodes.length);
      if (!groups.has(key)) groups.set(key, document.createDocumentFragment());
      groups.get(key).append(range.cloneContents());
    });
    for (const [key, content] of groups) {
      if (!content.textContent.trim()) continue;
      const chapter = chapters.querySelector(`[data-lca-chapter="${key}"]`);
      const body = chapter.querySelector('[data-lca-content]');
      body.append(content); this.refineContent(body, key); chapter.hidden = false;
    }
    source.replaceChildren(introduction);
    this.refineContent(source, 'overview');
    source.hidden = !source.textContent.trim();
    this.querySelector('[data-lca-overview]').hidden = source.hidden;
    chapters.hidden = false;
    this.addEventListener('toggle', event => {
      const opened = event.target;
      if (opened.open && opened.matches('[data-lca-chapter],[data-lca-overview]')) this.querySelectorAll('[data-lca-chapter],[data-lca-overview]').forEach(el => { if (el !== opened) el.open = false; });
    }, true);
    this.dataset.ready = 'true';
  }
  refineContent(body, key) {
    body.querySelectorAll('p').forEach(p => {
      if (!p.textContent.trim() && !p.querySelector('img,video,iframe')) p.remove();
    });
    if (key === 'faq') {
      const nodes = [...body.children];
      let answer;
      nodes.forEach(node => {
        const text = node.textContent.trim();
        const heading = /^H[1-6]$/.test(node.tagName);
        const strongQuestion = node.tagName === 'P' && node.children.length === 1 && ['STRONG', 'B'].includes(node.firstElementChild.tagName) && /[?？]$/.test(text);
        if (heading && /^(faq|questions|frequently asked)/i.test(text) && !text.endsWith('?')) { node.remove(); return; }
        if ((heading && text.endsWith('?')) || strongQuestion) {
          const question = document.createElement('details'); question.className = 'lca__question';
          const summary = document.createElement('summary');
          const label = document.createElement('span'); label.textContent = text;
          const plus = document.createElement('span'); plus.className = 'lca__plus'; plus.setAttribute('aria-hidden', 'true');
          summary.append(label, plus); answer = document.createElement('div'); answer.className = 'lca__answer';
          question.append(summary, answer); node.replaceWith(question);
        } else if (answer) answer.append(node);
      });
      return;
    }
    body.querySelectorAll('ul').forEach(list => {
      const items = [...list.children].filter(item => item.tagName === 'LI');
      const labelled = items.length && items.every(item => item.querySelector('strong,b'));
      list.classList.add(labelled ? 'lca__fact-grid' : 'lca__benefit-list');
    });
    body.querySelectorAll('ol').forEach(list => list.classList.add('lca__steps'));
    body.querySelectorAll('p').forEach(p => {
      if (p.children.length === 1 && ['STRONG', 'B'].includes(p.firstElementChild.tagName)) p.classList.add('lca__detail-label');
    });
    if (key === 'love') {
      const first = body.querySelector('p:not(.lca__detail-label)');
      if (first && !first.children.length) {
        const text = first.textContent.trim();
        const match = text.match(/^(.+?[.!?])\s+([\s\S]+)$/);
        first.classList.add('lca__editorial-lead');
        if (match) {
          first.textContent = match[1];
          const rest = document.createElement('p'); rest.textContent = match[2]; first.after(rest);
        }
      }
    }
  }
});
if (!customElements.get('lc-accessory-viewer')) customElements.define('lc-accessory-viewer', class extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const signal = this.controller.signal;
    const views = [...this.querySelectorAll('[data-lca-view]')];
    const controls = this.querySelector('.lca__view-controls');
    const caption = this.querySelector('[data-lca-caption]');
    const dialog = this.querySelector('dialog');
    controls.hidden = false;
    this.addEventListener('click', event => {
      const trigger = event.target.closest('[data-lca-show]');
      if (trigger) {
        views.forEach(view => { view.hidden = view.dataset.lcaView !== trigger.dataset.lcaShow; });
        controls.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button === trigger)));
        caption.textContent = views.find(view => !view.hidden)?.dataset.caption || '';
      }
      const view = event.target.closest('[data-lca-view]');
      if (view && dialog.showModal) {
        event.preventDefault(); this.lastTrigger = view;
        const img = document.createElement('img'); img.src = view.href; img.alt = view.dataset.caption;
        dialog.querySelector('.lca__lightbox-image').replaceChildren(img);
        dialog.querySelector('[data-lca-lightbox-caption]').textContent = view.dataset.caption;
        dialog.showModal();
      }
      if (event.target.closest('[data-lca-close]') || event.target === dialog) dialog.close();
    }, {signal});
    dialog.addEventListener('close', () => { this.lastTrigger?.focus(); }, {signal});
    let data;
    try { data = JSON.parse(this.querySelector('[data-lca-variants]').textContent); } catch { return; }
    document.addEventListener('variant:change', event => {
      const variant = data.variants.find(item => String(item.id) === String(event.detail?.variant?.id));
      if (!variant) return;
      const view = this.querySelector('[data-lca-view="whole"]');
      const img = view.querySelector('img');
      img.removeAttribute('srcset'); img.src = variant.image;
      img.alt = `${data.title} · ${variant.title}`;
      view.href = variant.image; view.dataset.caption = img.alt;
      if (!view.hidden) caption.textContent = img.alt;
    }, {signal});
  }
  disconnectedCallback() { this.controller?.abort(); }
});
if (!customElements.get('lc-accessory-rewards')) customElements.define('lc-accessory-rewards', class extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const signal = this.controller.signal;
    const points = this.querySelector('[data-points]'), tier = this.querySelector('[data-reward-tier]');
    if (!points || !tier) return;
    let price = Number(this.dataset.price);
    const update = () => { points.textContent = Math.floor(price / 100 * Number(tier.value)); };
    tier.addEventListener('change', update, {signal});
    this.closest('.lca-product')?.addEventListener('variant:change', event => {
      if (event.detail?.variant?.price != null) { price = Number(event.detail.variant.price); update(); }
    }, {signal});
    update();
  }
  disconnectedCallback() { this.controller?.abort(); }
});


if (!customElements.get('lc-accessory-story')) customElements.define('lc-accessory-story', class extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const signal = this.controller.signal;
    this.querySelectorAll('[data-lca-tutorial]').forEach(details => {
      details.addEventListener('toggle', () => {
        const slot = details.querySelector('[data-lca-video-slot]');
        if (details.open && !slot.children.length) slot.append(details.querySelector('template').content.cloneNode(true));
        if (!details.open) slot.replaceChildren();
      }, {signal});
    });
    let data;
    try { data = JSON.parse(this.querySelector('[data-lca-story-variants]').textContent); } catch { return; }
    document.addEventListener('variant:change', event => {
      const variant = data.variants.find(item => String(item.id) === String(event.detail?.variant?.id));
      if (!variant) return;
      this.querySelector('[data-lca-choice]').textContent = variant.title;
      this.querySelector('[data-lca-choice-price]').textContent = new DOMParser().parseFromString(variant.price, 'text/html').body.textContent;
      const piece = this.querySelector('.lca-story__piece');
      if (piece && !piece.querySelector('p')) {
        const image = piece.querySelector('img');
        image.removeAttribute('srcset'); image.src = variant.image; image.alt = variant.title;
      }
    }, {signal});
  }
  disconnectedCallback() { this.controller?.abort(); }
});

(() => {
  const styleId = 'lc-accessory-restock-alignment';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .lca-product .product-info__block-list.lc-accessory-has-restock-status > .product-info__block-item[data-block-type="inventory"] { display:none!important; }
      .lca-product .lc-restock-row:not([hidden]) { display:flex;align-items:center;flex-wrap:wrap;gap:12px 16px; }
      .lca-product .lc-restock-open { flex:0 0 auto; }
      .lca-product .lc-accessory-restock-status { display:flex;align-items:center;gap:8px;min-width:0;font:400 12px/1.5 Muli,Arial,sans-serif;color:#9d554c; }
      .lca-product .lc-accessory-restock-status::before { content:"";flex:0 0 5px;width:5px;height:5px;border-radius:50%;background:currentColor; }
      .lca-product .lc-accessory-restock-status.text-warning { color:#967849; }
      .lca-product .lc-accessory-restock-status.text-success { color:#6e7568; }
    `;
    document.head.append(style);
  }

  function connectAccessoryRestock(section) {
    if (section.dataset.lcAccessoryRestockAligned === 'true') return;
    section.dataset.lcAccessoryRestockAligned = 'true';
    let frame = 0;
    function sync() {
      frame = 0;
      const list = section.querySelector('.product-info__block-list');
      if (!list) return;
      const row = list.querySelector('.lc-restock-row:not([hidden])');
      const inventory = list.querySelector('[data-block-type="inventory"] variant-inventory');
      const source = inventory?.querySelector('span');
      const text = source?.textContent.trim() || '';
      const active = Boolean(row && text);
      list.classList.toggle('lc-accessory-has-restock-status', active);
      if (!active) return;

      let status = row.querySelector('.lc-accessory-restock-status');
      if (!status) {
        status = document.createElement('span');
        status.className = 'lc-accessory-restock-status';
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        row.append(status);
      }
      if (status.firstElementChild?.outerHTML !== source.outerHTML) status.replaceChildren(source.cloneNode(true));
      for (const state of ['error', 'warning', 'success']) {
        status.classList.toggle('text-' + state, inventory.classList.contains('text-' + state));
      }
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(sync);
    }
    new MutationObserver(schedule).observe(section, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'value', 'disabled']
    });
    sync();
  }

  function init() {
    document.querySelectorAll('.lca-product').forEach(connectAccessoryRestock);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once:true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();

